/*
 *   Copyright 2022-2024 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h } from "@stencil/core";
import { Param } from "../../model/param";
import { Logger } from "../../utils/logger";
import { GTSLib } from "../../utils/gts.lib";
import { Utils } from "../../utils/utils";
import { isEqual } from "lodash";
export class DiscoveryHidden {
    constructor() {
        this.divider = 1000;
        this.result = undefined;
        this.type = undefined;
        this.options = new Param();
        this.width = undefined;
        this.height = undefined;
        this.debug = false;
        this.unit = '';
        this.parsing = false;
        this.rendering = false;
        this.message = undefined;
        this.innerStyle = undefined;
        this.innerOptions = undefined;
        this.initial = false;
    }
    updateRes() {
        this.result = GTSLib.getData(this.result);
    }
    optionsUpdate(newValue, oldValue) {
        var _a, _b;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['optionsUpdate'], newValue, oldValue);
        let opts = newValue;
        if (!!newValue && typeof newValue === 'string') {
            opts = JSON.parse(newValue);
        }
        if (!isEqual(opts, this.innerOptions)) {
            this.innerOptions = Object.assign({}, opts);
            (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['optionsUpdate 2'], { options: this.innerOptions, newValue, oldValue });
        }
    }
    componentWillLoad() {
        var _a;
        this.parsing = true;
        this.LOG = new Logger(DiscoveryHidden, this.debug);
        if (typeof this.options === 'string') {
            this.innerOptions = JSON.parse(this.options);
        }
        else {
            this.innerOptions = this.options;
        }
        this.result = GTSLib.getData(this.result);
        this.divider = GTSLib.getDivider(this.innerOptions.timeUnit || 'us');
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentWillLoad'], {
            type: this.type,
            options: this.innerOptions,
        });
    }
    componentDidLoad() {
        setTimeout(() => {
            this.height = Utils.getContentBounds(this.el.parentElement).h;
            this.initial = true;
            this.parsing = false;
            setTimeout(() => this.draw.emit(), 100);
        });
    }
    async resize() {
        // empty
    }
    render() {
        return h("div", { key: 'fce23f9e87e85e697c65207072ed55016fb09c29', style: { display: 'hidden' } }, h("div", { key: '551c501c8733a05dcba5aac30419ee5eaa04a075', ref: (el) => this.wrapper = el }));
    }
    static get is() { return "discovery-hidden"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-hidden.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-hidden.css"]
        };
    }
    static get properties() {
        return {
            "result": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "DataModel | string",
                    "resolved": "DataModel | string",
                    "references": {
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "result",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChartType",
                    "resolved": "\"line\" | \"area\" | \"scatter\" | \"step-area\" | \"spline-area\" | \"spline\" | \"step\" | \"step-after\" | \"step-before\" | \"annotation\" | \"bar\" | \"bar-polar\" | \"display\" | \"image\" | \"map\" | \"gauge\" | \"linear-gauge\" | \"circle\" | \"compass\" | \"pie\" | \"doughnut\" | \"rose\" | \"tabular\" | \"svg\" | \"input:text\" | \"input:list\" | \"input:secret\" | \"input:autocomplete\" | \"input:chips\" | \"input:chips-autocomplete\" | \"input:slider\" | \"input:date\" | \"input:date-range\" | \"input:multi\" | \"input:multi-cb\" | \"button\" | \"button:radio\" | \"hidden\" | \"calendar\" | \"heatmap\" | \"profile\" | \"boxplot\" | \"dashboard\" | \"dashboard:flex\" | \"dashboard:scada\"",
                    "references": {
                        "ChartType": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::ChartType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "type",
                "reflect": false
            },
            "options": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "new Param()"
            },
            "width": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "width",
                "reflect": false
            },
            "height": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "height",
                "reflect": false
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            },
            "unit": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "unit",
                "reflect": false,
                "defaultValue": "''"
            }
        };
    }
    static get states() {
        return {
            "width": {},
            "height": {},
            "parsing": {},
            "rendering": {},
            "message": {},
            "innerStyle": {},
            "innerOptions": {},
            "initial": {}
        };
    }
    static get events() {
        return [{
                "method": "draw",
                "name": "draw",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "resize": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "result",
                "methodName": "updateRes"
            }, {
                "propName": "options",
                "methodName": "optionsUpdate"
            }];
    }
}
//# sourceMappingURL=discovery-hidden.js.map
